import { useState, type FormEvent } from 'react';
import type { Locale } from '@iac/contracts';
import type { Dictionary } from '../i18n/dictionary';
import { toast } from '../lib/toast';
import type { Story } from '../lib/story';
import { Dialog } from './Dialog';
import { Field } from './Field';

const sample: Dictionary<{
  open: string;
  title: string;
  name: string;
  balance: string;
  cancel: string;
  save: string;
  saved: (name: string) => string;
}> = {
  pl: {
    open: 'Edytuj portfel',
    title: 'Edycja: Oszczędności',
    name: 'Nazwa',
    balance: 'Saldo',
    cancel: 'Anuluj',
    save: 'Zapisz',
    saved: (name) => `Zapisano „${name}”`,
  },
  en: {
    open: 'Edit wallet',
    title: 'Edit: Savings',
    name: 'Name',
    balance: 'Balance',
    cancel: 'Cancel',
    save: 'Save',
    saved: (name) => `Saved "${name}"`,
  },
};

function Demo({ locale }: { locale: Locale }) {
  const t = sample[locale];
  const [open, setOpen] = useState(false);

  return (
    <>
      <button type="button" className="btn-ghost" onClick={() => setOpen(true)}>
        {t.open}
      </button>
      <Dialog open={open} title={t.title} onClose={() => setOpen(false)}>
        <DemoForm
          locale={locale}
          onDone={(name) => {
            toast.success(t.saved(name));
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      </Dialog>
    </>
  );
}

function DemoForm({
  locale,
  onDone,
  onCancel,
}: {
  locale: Locale;
  onDone: (name: string) => void;
  onCancel: () => void;
}) {
  const t = sample[locale];
  const [name, setName] = useState(
    locale === 'pl' ? 'Oszczędności' : 'Savings',
  );
  const [balance, setBalance] = useState('18 000,00');

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onDone(name);
  }

  return (
    <form onSubmit={submit} className="form-stack">
      <Field
        id="story-dialog-name"
        label={t.name}
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Field
        id="story-dialog-balance"
        label={t.balance}
        inputMode="decimal"
        value={balance}
        onChange={(e) => setBalance(e.target.value)}
      />
      <div className="form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          {t.cancel}
        </button>
        <button type="submit" className="btn-primary">
          {t.save}
        </button>
      </div>
    </form>
  );
}

export default {
  kind: 'component',
  title: { pl: 'Okno dialogowe', en: 'Dialog' },
  description: {
    pl: 'Okno modalne na natywnym `<dialog>`, w którym dodaje się i edytuje rekordy — zawsze tym samym formularzem. Fokus zostaje w środku, Escape, Anuluj albo kliknięcie w tło zamykają je i oddają fokus przyciskowi, który je otworzył. Treść montuje się dopiero przy otwarciu, więc każde otwarcie zaczyna od świeżego stanu. Poniżej md wysuwa się od dołu na całą szerokość ekranu.',
    en: 'A modal on the native `<dialog>`, where records are added and edited — always with the same form. Focus stays inside; Escape, Cancel or a press on the backdrop close it and return focus to the button that opened it. Its content mounts only on open, so each opening starts from fresh state. Below md it slides up from the bottom, full width.',
  },
  variants: [
    {
      name: { pl: 'Formularz', en: 'Form' },
      render: (locale) => <Demo locale={locale} />,
    },
  ],
} satisfies Story;
